<template>
	<b-modal
		v-model="viewFile"
		id="editimage"
		centered
		hide-footer
		size="lg"
		no-close-on-backdrop
		:title="!editFile ? 'View Video' : 'Edit File'"
	>
		<section class="position-absolute viewimage-header-icons mr-2">
			<img
				v-if="!editFile && $route.name !== 'coursecreation'"
				@click="handleEditClick"
				src="@/assets/common/edit.svg"
				alt="Edit"
				class="mr-2 pointer"
			/>
			<img
				v-if="!editFile && $route.name !== 'coursecreation'"
				@click="handleDeleteClick"
				src="@/assets/common/delete.svg"
				alt="Delete"
				class="mr-2 pr-2 ab-right-line pointer"
			/>
			<img
				@click="closeModal('viewFile')"
				class="rotate-on-hover pointer"
				src="@/assets/common/blue-cross.svg"
				alt="Close"
			/>
		</section>

		<section class="p-4" v-if="!editFile">
			<div class="header-container" style="background: #eef3f7">
				<h1 class="fw-500">{{ showDetails.title }}</h1>
			</div>
			<span
				class="ab-lv-tags mt-3 mr-2" style="display: inline-block"
				v-for="(tag, index) in showDetails.tags"
				:key="index"
			>
				#{{ tag }}
			</span>
			<span v-if="showDetails.asset">
				<VideoPlayer
					ref="lessonVideo"
					class="br-4 overflow-hidden mb-2 w-100 mt-3 video-height"
					:poster="showDetails.assetThumbnail"
					:src="showDetails.asset"
				/>
			</span>
		</section>
		<section v-else class="edit-modal-height">
			<Files
				ref="getFiles"
				:fileData="showDetails"
				:validation="validation"
				@fileUpload="fileUpload"
				:uploadProgress="viewProgress?.previewVideo"
				:uploadfileExtension="'mp4'"
				:editFile="editFile"
				:errorFile="errorFile"
				:uploadTime="timeRemaining"
				:thumbnailPresent="thumbnailPresent"
				@getThumbnail="getThumbnail"
				@deleteFile="deleteFile"
				:uploadId="uploadVideoData._id"
				@deleteId="uploadVideoData._id = ''"
				@resetData="resetData"
				@showLoader="showLoader = false"
				:editSpinner="editSpinner"
				:noVideo="noVideo"
			/>
		</section>
		<hr v-if="editFile" />

		<div
			class="d-flex align-items-center justify-content-between px-3 pb-3"
			v-if="editFile"
		>
			<div>
				<OButton
					class="p1-15"
					type="add"
					text="Thumbnail"
					:disabled="
						thumbnailPresent ||
						(!uploadedFileData &&
							acceptedVideoTypes.some((ext) =>
								uploadedFileData?.fileName?.endsWith(ext)
							)) ||
						showDetails.assetThumbnail !== null ||
						(showDetails.assetType !== '1' && !showThumbnail) ||
						(progress > 0 && progress < 100)
					"
					@clicked="handleThumbnailClick"
				/>
			</div>

			<div class="d-flex align-items-center">
				<OButton class="p1-15" @clicked="handleCancel" text="Cancel" />
				<PButton
					:loading="loading || showLoader"
					widthClass="button-width"
					class="ml-2 p1-15"
					text="Update"
					:disabled="progress > 0 && progress < 100"
					@clicked="handleUpdateClick"
				/>
			</div>
		</div>
	</b-modal>
</template>

<script>
import Files from "./Files.vue";
import { getBucket } from "@/views/course-creation/api-client/courseInfoAPI";
import { postAsset } from "@/views/asset-bank/api-client/assets";
import { eventBus } from "@/utils/eventBus";
import fileUpload from "@/mixins/fileUpload";
import toast from "@/mixins/toast";
import { mapGetters } from "vuex";

export default {
	mixins: [fileUpload, toast],

	components: {
		Files,
	},
	props: {
		viewFile: Boolean,
		editFile: Boolean,
		showDetails: Object,
		validation: Object,
		errorFile: Boolean,
		thumbnailPresent: Boolean,
		loading: Boolean,
	},
	data() {
		return {
			isCancelled: false,
			acceptedVideoTypes: ["mp4", "mov", "webm"],
			editSpinner: false,
			progress: 0,
			viewProgress: {
				thumbnail: 0,
				previewVideo: 0,
			},
			showLoader: false,
			uploadVideoData: {},
			showThumbnail: false,
			uploadedFileData: {},
			timeRemaining: "",
			thumbnailData: {
				fileName: "",
				previewKey: "",
				sourceKey: "",
			},
			noVideo: false,
		};
	},
	computed: {
		...mapGetters("assetVUEX", {
			getfiles: "getUploadedFiles",
		}),
		isThumbnailDisabled() {
			return (
				this.thumbnailPresent ||
				(!this.uploadedFileData &&
					this.acceptedVideoTypes.some((ext) =>
						this.uploadedFileData.fileName.endsWith(ext)
					)) ||
				this.showDetails.assetThumbnail !== null ||
				this.showDetails.assetType !== "1"
			);
		},
		isMp4File() {
			return (
				this.showDetails.asset &&
				this.acceptedVideoTypes.some((ext) =>
					this.showDetails.asset.endsWith(ext)
				)
			);
		},
		progressForCurrentFile() {
			if (!this.isMp4File) {
				return;
			}
			const file = this.getfiles.find(
				(f) => f.fileData.tempId === this.showDetails.tempId
			);

			return file ? file.progress : 0;
		},
	},
	watch: {
		viewFile() {
			if (this.editFile) this.resetData();
		},
		progress(newVal) {
			if (newVal > 0) {
				this.editSpinner = false;
			}
		},
		progressForCurrentFile(newProgress) {
			if (newProgress > 0 && this.viewFile) {
				this.closeModal("viewFile");
				eventBus.$emit("show-upload-list");
			}
		},
	},
	methods: {
		resetData() {
			this.timeRemaining = "";
			this.progress = 0;
			this.isCancelled = false;
		},
		handleEditClick() {
			this.$emit("changeVal", "editFile");
			this.$emit("closeModal", "thumbnailPresent");
		},
		handleDeleteClick() {
			this.$emit("changeVal", "delete", "viewFile");
		},

		closeModal(context) {
			this.$emit("closeModal", context);
		},

		handleCancel() {
			this.$refs.getFiles.disconnectSocket();
			this.closeModal("viewFile");
			if (this.isMp4File) {
				this.$store.commit(
					"assetVUEX/REMOVE_COMPLETED_FILE_USING_TEMP_ID",
					this.showDetails.tempId
				);
			}
			this.isCancelled = true;
			this.uploadedFileData = {};
			this.uploadVideoData = {};
		},

		async handleUpdateClick() {
			if (
				!this.showDetails.asset &&
				!Object.keys(this.uploadedFileData).length
			) {
				this.noVideo = true;
				return;
			}
			if (
				Object.keys(this.uploadedFileData).length !== 0 ||
				this.thumbnailData.sourceKey
			) {
				await this.processFiles();
			} else if (
				Object.keys(this.uploadedFileData).length === 0 ||
				!this.thumbnailData.sourceKey
			) {
				this.$emit("handle-save");
			}
		},

		async processFiles() {
			let videoBucketPath;
			let thumbnailBucketPath;
			this.editSpinner = true;
			if (this.isMp4File) {
				this.showDetails.tempId =
					Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
			}

			try {
				this.showLoader = true;
				this.isVideoPresent = true;
				this.uploadStatus = "Getting file, please wait...";
				let payload = {
					assetType: String(this.showDetails.assetType),
				};
				if (Object.keys(this.uploadedFileData).length !== 0) {
					const response = await getBucket(payload);
					if (response.status === 200) {
						if (payload.assetType == "1") {
							this.$store.commit("assetVUEX/GET_FILE_DATA", this.showDetails);
							this.$store.dispatch("assetVUEX/addUploadedFile", {
								fileName: this.uploadedFileData.fileName,
								videoLength: this.uploadedFileData.videoLength,
								previewKey: this.uploadedFileData.previewKey,
								fileSize: this.uploadedFileData.size,
								sourceKey: this.uploadedFileData.sourceKey,
								fileData: this.showDetails,
							});
							this.fileIndex = this.getfiles.length - 1;
							videoBucketPath = await this.calculateHash(
								this.uploadedFileData.files,
								response.data.data,
								this.fileIndex,
								this.showDetails,
								true
							);
						} else {
							this.calculateHash(
								this.uploadedFileData.files,
								response.data.data
							);
						}
					}
				}

				if (this.thumbnailData.sourceKey) {
					let payload = {
						assetType: "3",
					};
					const response = await getBucket(payload);
					if (response.status === 200) {
						thumbnailBucketPath = await this.calculateHash(
							this.thumbnailData.sourceKey,
							response.data.data,
							"",
							"",
							true
						);
					}
				}
				if (payload.assetType == "1")
					this.saveVideoAsset(videoBucketPath, thumbnailBucketPath);
			} catch (error) {
				this.showLoader = false;
				console.error(error);
			}
		},

		async saveVideoAsset(fileBucketPath, thumbnailBucketPath = null) {
			if (this.isCancelled) {
				return;
			}
			try {
				let payload = {
					title: this.showDetails.title,
					assetType: String(this.showDetails.assetType),
					tags: JSON.stringify(this.showDetails.tags),
					assetLibrary: "true",
					assetID: this.showDetails.assetID,
				};

				if (this.uploadedFileData.files !== undefined) {
					payload.asset = {
						uploadType: "1",
						path: fileBucketPath,
					};
				}
				if (payload.assetType == "1") {
					payload.durationInSec = Math.floor(this.uploadedFileData.videoLength);
					if (this.thumbnailData.sourceKey) {
						payload.assetThumbnail = {
							uploadType: "1",
							path: thumbnailBucketPath,
						};
					} else if (
						!this.thumbnailData.sourceKey &&
						!this.showDetails.assetThumbnail
					) {
						payload.assetThumbnail = {
							uploadType: "4",
							path: null,
						};
					}
				}

				const response = await postAsset(payload, "put");
				if (response.status === 200) {
					this.showLoader = false;
					this.deleteFile();
					eventBus.$emit("assetUploadSuccess");
					this.$emit("handle-get");
					this.closeModal("viewFile");
					this.isVideoUploading = false;
					this.uploadStatus = "";
					this.$nextTick(() => {
						this.successToast("Asset updated successfully!");
					});
					return response.data.data;
				} else {
					this.editSpinner = false;
				}
			} catch (error) {
				this.showLoader = false;
				console.error(error);
			}
		},

		deleteFile() {
			this.$emit("deleteFile");
			this.thumbnailData = {
				fileName: "",
				previewKey: "",
				sourceKey: "",
			};
			this.uploadVideoData = {};
			this.uploadedFileData = {};
		},

		getThumbnail(file) {
			this.thumbnailData.sourceKey = file;
			this.$emit("getThumbnail", file);
		},

		fileUpload(file) {
			this.uploadedFileData = file;
			if (
				this.acceptedVideoTypes.some((ext) => file.fileName?.endsWith(ext)) &&
				this.showDetails.assetType === "1"
			) {
				this.showThumbnail = true;
			}
			this.$emit("fileUploaded", file);
			if (this.thumbnailData.sourceKey !== "") {
				this.$emit("changeVal", "thumbnailPresent");
			} else {
				this.$emit("closeModal", "thumbnailPresent");
			}
		},
		handleThumbnailClick() {
			this.$emit("changeVal", "thumbnailPresent");
			this.$refs.getFiles.uploadThumbnail = false;
		},
	},
};
</script>
<style>
.video-height {
	height: calc(100vh - 485px);
}
.edit-modal-height {
	overflow: auto;
	height: calc(100vh - 300px);
}
</style>
